import { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useDetectClickOutside } from 'react-detect-click-outside';

import debounce from 'debounce';
import { useLazyQuery } from '@apollo/client';

import { Loader } from '@/components/base/Layout/Loader';
import { useListingSearchStore } from '@/store';
import { SearchLocationQuery } from '@/query';

import styles from './LocationDropdown.module.css';
import { i18nMap, IconsPalette } from '../../constants';

const LocationCategory = ({ type }) => {
  const t = useTranslations();

  return <span className={styles.status}>{t(i18nMap[type])}</span>;
};

export const LocationDropdown = ({ locationValue, closeModal, setValue }) => {
  const [list, setList] = useState([]);
  const [firstRequestReady, setFirstRequestReady] = useState(false);

  const { setLocation, location } = useListingSearchStore();

  const handleCloseModal = (e) => {
    if (closeModal) closeModal(e);
  };

  const handleClick = (value) => {
    setLocation(value);
    setValue(value.name);
    if (closeModal) closeModal();
  };

  const ref = useDetectClickOutside({ onTriggered: handleCloseModal });
  const [getLocation, { data, loading, error }] = useLazyQuery(
    SearchLocationQuery,
    {
      onError: (err) => {
        const wasAborted = String(err).toLowerCase().includes('abort');
        if (!wasAborted) {
          setFirstRequestReady(true);
        }
      },
      onCompleted: (data) => {
        setList(data.locationAutocomplete.collection);
        setFirstRequestReady(true);
      },
    }
  );

  const findLocation = useCallback(debounce(getLocation, 300), []);

  useEffect(() => {
    const aborter = new AbortController();
    findLocation({
      variables: {
        locationName: locationValue,
      },
      context: {
        fetchOptions: {
          signal: aborter.signal,
        },
      },
    });
    return () => aborter.abort();
  }, [locationValue]);

  const showLoader = (loading || !data) && !firstRequestReady;
  const noResults =
    !list.length && locationValue.length > 0 && firstRequestReady && !error;
  if (noResults) {
    return null;
  }
  return (
    <div className={styles.location_dropdown} ref={ref}>
      <ul role="listbox">
        {!showLoader &&
          list.map((value) => (
            <li
              key={value.name}
              className={`${styles.location_row} ${
                location?.name === value.name ? styles.location_row_active : ''
              } `}
              onClick={() => handleClick(value)}
              role="option"
            >
              <div className={styles.icon} type="button">
                {IconsPalette[value.type]}
              </div>
              <div className={styles.content}>
                <LocationCategory type={value.type} />
                <p title={value.name}>{value.name}</p>
              </div>
            </li>
          ))}
        {showLoader && (
          <li role="option">
            <Loader />
          </li>
        )}
      </ul>
    </div>
  );
};
